import type { DocPageContent } from '../docs-content'

const page = {
  order: 18,
  slug: 'export',
  title: '导出',
  description:
    '在浏览器中渲染视频或音频、选择格式与范围，并清除起飞前警告。',
  category: '输出',
  related: ['render-queue', 'text-captions-subtitles', 'troubleshooting'],
  sections: [
    {
      title: '设置导出',
      blocks: [
        {
          kind: 'steps',
          items: [
            '打开**导出**（`Ctrl+Shift+E`），并选择**导出视频**或**导出音频**。',
            '渲染整个项目，或在只需要某一段时渲染当前的入/出范围。',
            '设置**分辨率**与**质量**以匹配目标：快速预览、可分享文件，或最终母版。',
            '清除所有起飞前警告，然后开始渲染。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '更高的分辨率和质量耗时更长，生成的文件也更大。',
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '当项目包含主时间轴之外的其它时间轴时，对话框顶部的**序列**菜单可选择要导出的时间轴——使用其自身的分辨率与范围。',
        },
      ],
    },
    {
      title: '格式',
      blocks: [
        {
          kind: 'table',
          headers: ['输出', '格式'],
          rows: [
            ['视频容器', 'MP4、MOV、WebM、MKV'],
            ['仅音频', 'MP3、AAC、WAV'],
            ['软字幕轨', 'WebM、MKV'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '可用的编解码器取决于你的浏览器；当某个不可用时光剪会发出警告或回退。',
        },
      ],
    },
    {
      title: '副标题',
      blocks: [
        {
          kind: 'paragraph',
          text: '当视频导出带有转录稿时，**副标题**控件决定字幕如何被包含。选项会随你所选的容器而变化。',
        },
        {
          kind: 'table',
          headers: ['模式', '作用'],
          rows: [
            ['**关闭**', '导出视频中不包含字幕。'],
            ['**烧录**', '字幕被绘制到画面中，因此在所有播放器中都会显示。'],
            ['**外挂** `.srt`', '干净的视频，外加一个随附下载的独立 `.srt` 字幕文件。'],
            ['**内嵌轨**', '一条封装在容器内的、可开关的软字幕轨。'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '内嵌轨选项仅针对 `WebM` 和 `MKV` 出现。在 `MP4` 和 `MOV` 上，请选择烧录或外挂文件，或者更换容器以获得可开关的轨道。',
        },
        {
          kind: 'note',
          tone: 'info',
          text: '某些播放器默认隐藏字幕轨，因此观众可能需要手动开启。',
        },
      ],
    },
    {
      title: '起飞前检查',
      blocks: [
        {
          kind: 'paragraph',
          text: '起飞前检查会在渲染前运行，并标记常见阻碍：空范围、缺失媒体、不可用的编解码器、worker 导出回退、大文件或长时间渲染。',
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '为快速确认，可先导出一小段入/出范围，再开始完整渲染。',
        },
      ],
    },
    {
      title: '渲染与下载',
      blocks: [
        {
          kind: 'list',
          items: [
            '进度依次经历准备、渲染、编码和完成阶段，你可以随时**取消**。',
            '在支持时，worker 导出会在后台运行；主线程回退则要求标签页保持聚焦且不被打扰。',
            '完成后，选择**下载**以保存文件，文件同时会写入你工作区中的导出文件夹。',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: '在导出完成前请保持项目标签页打开。若要批量渲染多个任务，请将它们加入**渲染队列**，而不是逐个导出。',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
